import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import NamedRedirect from '../NamedRedirect/NamedRedirect';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { createSlug } from '../../util/urlHelpers';
import {
  clearPostAuthIntent,
  getCheckoutDataFromStorage,
  resolvePostAuthRedirect,
} from '../../util/postAuthIntent';

const runCheckoutSetup = (
  history,
  routeConfiguration,
  callSetInitialValues,
  onInitializeCardPaymentData
) => {

  const checkoutData = getCheckoutDataFromStorage();
  const listing = checkoutData?.listing || {};
  const listingId = listing?.id?.uuid;
  if (!listingId) {
    return false;
  }

  const { displayName: authorName } = listing?.author?.attributes?.profile || {};
  const { setInitialValues } = findRouteByRouteName('CheckoutPage', routeConfiguration);

  if (setInitialValues && callSetInitialValues) {
    callSetInitialValues(setInitialValues, checkoutData, true);
  }
  if (onInitializeCardPaymentData) {
    onInitializeCardPaymentData();
  }

  history.push(
    createResourceLocatorString(
      'CheckoutPage',
      routeConfiguration,
      { id: listingId, slug: createSlug(authorName) },
      { bookingLink: true }
    )
  );
  return true;
};

const PostAuthRedirect = props => {
  const {
    user,
    userType,
    isBookingLink,
    routeConfiguration,
    history,
    callSetInitialValues,
    onInitializeCardPaymentData,
  } = props;

  const checkoutNavStarted = useRef(false);

  const target = resolvePostAuthRedirect({
    user,
    userType,
    isBookingLink,
    routeConfiguration,
  });

  useEffect(() => {
    if (target?.type !== 'checkout' || !target.setupCheckout || checkoutNavStarted.current) {
      return;
    }
    checkoutNavStarted.current = true;
    if (target.clearIntent) {
      clearPostAuthIntent();
    }
    const navigated = runCheckoutSetup(
      history,
      routeConfiguration,
      callSetInitialValues,
      onInitializeCardPaymentData
    );
    if (!navigated && target.clearIntent === false) {
      clearPostAuthIntent();
    }
  }, [
    target,
    history,
    routeConfiguration,
    callSetInitialValues,
    onInitializeCardPaymentData,
  ]);

  if (!target) {
    return null;
  }

  if (target.type === 'checkout' && target.setupCheckout) {
    return null;
  }

  if (target.clearIntent) {
    clearPostAuthIntent();
  }

  if (target.type === 'path') {
    return (
      <Redirect
        to={{
          pathname: target.pathname,
          search: target.search,
          hash: target.hash,
        }}
      />
    );
  }

  if (target.type === 'named') {
    return <NamedRedirect name={target.name} params={target.params || {}} />;
  }

  return null;
};

PostAuthRedirect.propTypes = {
  user: PropTypes.object.isRequired,
  userType: PropTypes.string.isRequired,
  isBookingLink: PropTypes.bool,
  routeConfiguration: PropTypes.array.isRequired,
  history: PropTypes.object.isRequired,
  callSetInitialValues: PropTypes.func,
  onInitializeCardPaymentData: PropTypes.func,
};

PostAuthRedirect.defaultProps = {
  isBookingLink: false,
  callSetInitialValues: null,
  onInitializeCardPaymentData: null,
};

export default PostAuthRedirect;
